import React, { useEffect, useMemo, useState } from 'react';
import { Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { studentsService } from '@/services/students';
import { bookingsService, type BookingDto } from '@/services/bookings';
import { tenantService } from '@/services/tenants';
import { teacherBillingService } from '@/services/teacherBilling';
import { useToast } from '@/components/ui/use-toast';
import {
  PromoCard,
  PromoCardBody,
  PromoCardDesc,
  PromoCardHeader,
  PromoCardTitle,
} from '@/components/promo/PromoCard';
import {
  formatInTimezone,
  getBrowserTimezone,
  utcDateKey,
  formatMoney,
} from '@/utils/datetime';

const TeacherDashboardPage: React.FC = () => {
  const { toast } = useToast();
  const [students, setStudents] = useState(0);
  const [bookings, setBookings] = useState<BookingDto[]>([]);
  const [tenantName, setTenantName] = useState('');
  const [revenueCents, setRevenueCents] = useState(0);
  const [currency, setCurrency] = useState('EUR');
  const [loading, setLoading] = useState(true);
  const tz = getBrowserTimezone();

  useEffect(() => {
    void loadAll();
  }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [studentsData, bookingsData, billing] = await Promise.all([
        studentsService.getStudents(),
        bookingsService.getBookings(),
        teacherBillingService.getPaymentsOverview().catch(() => null),
      ]);
      setStudents(Array.isArray(studentsData) ? studentsData.length : 0);
      setBookings(Array.isArray(bookingsData) ? bookingsData : []);
      if (billing) {
        setRevenueCents(billing.earnings.netEarningsCents);
        setCurrency(billing.earnings.currencyCode);
      }

      try {
        const tenant = await tenantService.getCurrentTenant();
        setTenantName(tenant.name || 'My studio');
      } catch {
        setTenantName('My studio');
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load dashboard',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const todayKey = utcDateKey(new Date().toISOString(), tz);
  const todayBookings = useMemo(
    () =>
      bookings
        .filter((b) => utcDateKey(b.startTime, tz) === todayKey)
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()),
    [bookings, todayKey, tz]
  );

  const weekStartKey = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay());
    return utcDateKey(d.toISOString(), tz);
  }, [tz]);

  const lessonsThisWeek = useMemo(() => {
    return bookings.filter((b) => {
      const key = utcDateKey(b.startTime, tz);
      const bDate = new Date(`${key}T12:00:00`);
      const wStart = new Date(`${weekStartKey}T12:00:00`);
      const wEnd = new Date(wStart);
      wEnd.setDate(wEnd.getDate() + 7);
      return bDate >= wStart && bDate < wEnd;
    }).length;
  }, [bookings, weekStartKey, tz]);

  const todayLabel = formatInTimezone(new Date().toISOString(), {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const durationMin = (b: BookingDto) => {
    const ms = new Date(b.endTime).getTime() - new Date(b.startTime).getTime();
    return Math.max(1, Math.round(ms / 60000));
  };

  return (
    <div className="space-y-4">
      <PromoCard>
        <PromoCardHeader>
          <PromoCardTitle icon={<Home size={20} strokeWidth={2} />}>Overview</PromoCardTitle>
          <PromoCardDesc>
            {tenantName || 'Your studio'} · times in {tz}
          </PromoCardDesc>
        </PromoCardHeader>
        <PromoCardBody>
          <div className="dash-stack">
            <div className="today-lessons">
              <div className="today-lessons-h">
                Today&apos;s lessons <span>{todayLabel}</span>
              </div>
              {loading ? (
                <p className="promo-empty">Loading…</p>
              ) : todayBookings.length === 0 ? (
                <p className="promo-empty">No lessons scheduled today</p>
              ) : (
                todayBookings.slice(0, 8).map((booking, i) => (
                  <div key={booking.id} className="today-lesson-row">
                    <div className="tl-main">
                      <div className="tl-title">
                        {booking.studentName || booking.title || 'Lesson'}
                      </div>
                      <div className="tl-meta">
                        {durationMin(booking)} min · Online · {booking.status}
                        {booking.studentTimezone && booking.studentTimezone !== tz
                          ? ` · student ${booking.studentTimezone}`
                          : ''}
                      </div>
                    </div>
                    <span className="tl-time">
                      {formatInTimezone(booking.startTime, {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </span>
                    {i === 0 && booking.status === 'confirmed' && (
                      <span className="tl-badge">Next</span>
                    )}
                  </div>
                ))
              )}
            </div>
            <div className="bstat-grid">
              <div className="bstat">
                <span className="lbl">Active students</span>
                <div className="val">{students}</div>
              </div>
              <div className="bstat">
                <span className="lbl">Lessons this week</span>
                <div className="val">{lessonsThisWeek}</div>
              </div>
              <div className="bstat">
                <span className="lbl">Net revenue (MTD)</span>
                <div className="val">{formatMoney(revenueCents, currency)}</div>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              <Link to="/students" className="text-violet-700 font-semibold hover:underline">
                Manage students
              </Link>
              {' · '}
              <Link to="/bookings" className="text-violet-700 font-semibold hover:underline">
                Open calendar
              </Link>
            </p>
          </div>
        </PromoCardBody>
      </PromoCard>
    </div>
  );
};

export default TeacherDashboardPage;
